import React from 'react';
import Image from '@/components/atoms/Image';
import Link from '@/components/atoms/link';
import { classnames } from '@/utils/classnames/classnames';
import { HeroBannerAlignment, HeroBannerCta, HeroBannerProps, HeroBannerTheme } from './types';

// Solid-colour fallback used when no background image is configured. `#1E5583`
// (navy) and `#415A77` (dark) from Dan's v0 mock map to the project's primary /
// secondary tokens — keeps colours on-token, no hardcoded hex.
const themeConfig: Record<HeroBannerTheme, { section: string; heading: string; eyebrow: string; subheading: string }> =
  {
    navy: { section: 'bg-primary', heading: 'text-white', eyebrow: 'text-white', subheading: 'text-white/90' },
    dark: { section: 'bg-secondary', heading: 'text-white', eyebrow: 'text-white', subheading: 'text-white/90' },
    white: { section: 'bg-white', heading: 'text-gray-800', eyebrow: 'text-primary', subheading: 'text-gray-600' },
  };

const alignmentConfig: Record<HeroBannerAlignment, string> = {
  'bottom-left': 'items-end justify-start pl-6 pb-8 md:pl-12 md:pb-12 lg:pl-20 lg:pb-16',
  'bottom-center': 'items-end justify-center pb-12 md:pb-16 lg:pb-20 px-6',
  center: 'items-center justify-center px-6',
  'center-left': 'items-center justify-start pl-6 md:pl-12 lg:pl-20',
};

// Border lives on each variant (not the base): a base `border-transparent` would win over a
// variant's `border-primary` by CSS source order, hiding the outline button on the light theme.
const ctaBase =
  'text-13 md:text-14 inline-flex items-center justify-center rounded-lg border-2 px-5 py-2.5 font-semibold transition-colors focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:outline-none md:px-6 md:py-3';

const isExternal = (href?: string) => !!href && /^(https?:|mailto:|tel:)/i.test(href);

const imageConfigured = (image?: HeroBannerProps['backgroundImage']) =>
  !!image && (!!image.media?.mediaId || !!image.src);

const CtaButton = ({ cta, className }: { cta: HeroBannerCta; className: string }) => {
  // No resolved href (e.g. an unresolved reference passed in by the Hero Carousel,
  // EWM-455) → omit the button rather than render a dead `href="#"` link.
  const href = cta.link.href;
  if (!href) return null;
  const label = cta.label ?? cta.link.name;
  const classNames = classnames(ctaBase, className);

  if (isExternal(href)) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" className={classNames}>
        {label}
      </a>
    );
  }

  return (
    <Link href={href} openInNewTab={cta.link.openInNewTab} underlineOnHover={false} className={classNames}>
      {label}
    </Link>
  );
};

/**
 * HeroBanner — full-width hero with an optional background image (or a solid-colour
 * theme fallback), overlay eyebrow / heading / subheading, and up to two CTAs
 * (primary = filled, secondary = outline). Responsive across breakpoints. Built as a
 * standalone, reusable slide so the Hero Carousel tastic (EWM-455) can compose it.
 *
 * Renders nothing when there is neither a heading nor a background image.
 */
const HeroBanner = ({
  backgroundImage,
  imageAlt,
  theme = 'navy',
  eyebrow,
  heading,
  subheading,
  alignment = 'bottom-left',
  overlayOpacity = 0.4,
  isPriority,
  imageLoading,
  primaryCta,
  secondaryCta,
}: HeroBannerProps) => {
  const hasImage = imageConfigured(backgroundImage);

  // Empty / unconfigured: no heading and no image → render nothing.
  if (!heading && !hasImage) return null;

  const t = themeConfig[theme];
  const onDark = hasImage || theme !== 'white';
  const centered = alignment === 'center' || alignment === 'bottom-center';

  // Over an image the scrim guarantees contrast, so text is always white; on the
  // solid-colour fallback the theme decides the text tone.
  const headingClass = hasImage ? 'text-white' : t.heading;
  const eyebrowClass = hasImage ? 'text-white' : t.eyebrow;
  const subheadingClass = hasImage ? 'text-white/90' : t.subheading;

  // On a dark solid fill (navy / dark, no image) a `bg-primary` button vanishes into the
  // section — invert it to a white fill. Over imagery the scrim gives `bg-primary` enough contrast.
  const darkSolid = !hasImage && theme !== 'white';
  const primaryClass = darkSolid
    ? 'border-transparent bg-white text-primary hover:bg-white/90 focus-visible:ring-white'
    : 'border-transparent bg-primary text-white hover:bg-primary-hover focus-visible:ring-primary';
  const secondaryClass = onDark
    ? 'hover:text-primary border-white text-white hover:bg-white focus-visible:ring-white'
    : 'border-primary text-primary hover:bg-primary focus-visible:ring-primary hover:text-white';

  return (
    <section
      className={classnames('relative h-[300px] w-full overflow-hidden md:h-[400px] lg:h-[500px]', {
        [t.section]: !hasImage,
      })}
    >
      {hasImage && (
        <>
          <Image
            {...backgroundImage}
            alt={imageAlt ?? heading ?? ''}
            fill
            sizes="100vw"
            style={{ objectFit: 'cover' }}
            priority={isPriority}
            // `loading` and `priority` are mutually exclusive in next/image — priority
            // already loads eagerly, so only forward `loading` for non-priority slides.
            {...(isPriority ? {} : { loading: imageLoading })}
            fetchPriority={isPriority ? 'high' : 'auto'}
            className="absolute inset-0"
          />
          <div className="absolute inset-0 bg-black" style={{ opacity: overlayOpacity }} aria-hidden />
        </>
      )}

      <div className={classnames('relative z-10 flex h-full', alignmentConfig[alignment])}>
        <div className={classnames('max-w-2xl', { 'text-center': centered })}>
          {eyebrow && (
            <span
              className={classnames(
                'text-12 mb-2 inline-block font-semibold tracking-wide uppercase',
                { 'drop-shadow-md': hasImage },
                eyebrowClass,
              )}
            >
              {eyebrow}
            </span>
          )}

          {heading && (
            <h2
              className={classnames(
                'text-24 md:text-36 lg:text-48 mb-2 leading-tight font-bold md:mb-3 lg:mb-4',
                { 'drop-shadow-lg': hasImage },
                headingClass,
              )}
            >
              {heading}
            </h2>
          )}

          {subheading && (
            <p
              className={classnames(
                'text-14 md:text-16 lg:text-18 mb-4 max-w-xl leading-normal md:mb-6 lg:mb-8',
                { 'mx-auto': centered, 'drop-shadow-md': hasImage },
                subheadingClass,
              )}
            >
              {subheading}
            </p>
          )}

          {(primaryCta || secondaryCta) && (
            <div className={classnames('flex flex-wrap gap-3 md:gap-4', { 'justify-center': centered })}>
              {primaryCta && <CtaButton cta={primaryCta} className={primaryClass} />}
              {secondaryCta && <CtaButton cta={secondaryCta} className={secondaryClass} />}
            </div>
          )}
        </div>
      </div>
    </section>
  );
};

export default HeroBanner;
